import { HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AuthService } from '@core/services/auth.service';
import { environment } from '@env/environment';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AlertService } from '../services/alert.service';

@Injectable()
export class AuthTokenInterceptor implements HttpInterceptor {
  refreshTokenInProgress = false;
  refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  constructor(private authService: AuthService, private alertService: AlertService) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    request = this.authToken(request);
    return next.handle(request).pipe(
      catchError(error => {
        if (request.url.includes('refresh-token') || request.url.includes('login')) {
          this.logout(request);
         return throwError(() => error); 
        }

        if (error.status !== 401) {
           return throwError(() => error); 
        }

        if (this.isPublicHelpRequest(request) && !this.getToken()) {
          return throwError(() => error);
        }

        const isSso = sessionStorage.getItem('isSSO');
        if(isSso === 'yes'){
          this.authService.navigateFederated();
        }else
        if (this.refreshTokenInProgress) {
          return this.refreshTokenSubject.pipe(
            filter(token => token !== null),
            take(1),
            switchMap(_token => next.handle(this.setAuthToken(request)))
          );
        } else {
          this.refreshTokenInProgress = true;
          return this.authService.refreshToken().pipe(
            switchMap((token: any) => {
              //When the call to refreshToken completes we reset the refreshTokenInProgress to false
              // for the next time the token needs to be refreshed
              this.refreshTokenInProgress = false;
              this.refreshTokenSubject.next(token);

              return next.handle(this.setAuthToken(request));
            }),
            catchError((err: any) => {
              if (!err.url.includes(environment.mstrURL)) {
                this.refreshTokenInProgress = false;
                this.authService.logout();
              } else {
                this.alertService.error(`Failed to connect ${environment.mstrURL}`);
              }
               return throwError(() => error); 
            })
          );
        }
      })
    );
  }

  private logout(request: HttpRequest<unknown>) {
    if (request.url.includes('refresh-token')) {
      this.authService.logout();
    }
  }

  private authToken(request: HttpRequest<unknown>) {
    if (!request.url.includes('.s3.') && !request.url.includes(environment.mstrURL)) {
      if (!this.getToken() && this.isPublicHelpRequest(request)) {
        return request;
      }
      request = this.setAuthToken(request);
    }
    return request;
  }

  private isPublicHelpRequest(request: HttpRequest<unknown>): boolean {
    return request.url.includes('getTrainingRoleConfig') || request.url.includes('/file/document-view')  || request.url.includes('getTrainingConfig') || 
    request.url.includes('/useraccess/document-view');
  }

  setAuthToken(request) {
    let roleid = sessionStorage.getItem('role_id') !== undefined ? sessionStorage.getItem('role_id') : null;
    let rolename = sessionStorage.getItem('role_name') !== undefined ? sessionStorage.getItem('role_name') : null;
    if (roleid && rolename) {
      const headers = new HttpHeaders(
        { 'Authorization': `Bearer ${this.getToken()}`, 'portal': `${this.getRoles()}`, 'roleid': `${roleid}`, 'rolename': `${rolename}` }
      );
      return request.clone({
        headers
      });
    } else {
      if (request.url.includes('/oauth2/token')) {
        const headers = new HttpHeaders(
          {'Content-Type': 'application/x-www-form-urlencoded' }
        );
        return request.clone({
          headers
        });
      } else if (request.url.includes('oauth2/userInfo')) {
          return request;
        } else {
        const headers = new HttpHeaders(
      {'Authorization': `Bearer ${this.getToken()}`,'portal':`${this.getRoles()}`}
    );
    
    return request.clone({
      headers
    });
   }
  }
  }

  isTokenExpired() {
    const helper = new JwtHelperService();
    return this.getToken() && helper.isTokenExpired(this.getToken());
  }

  getToken(): any {
    return sessionStorage.getItem('jwt-token');
  }

  getRoles(): any {
    const userData:any = JSON.parse(sessionStorage.getItem('user'));
    if(userData) {
      return userData.portalname;
    }
  }
}
